import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { track } from '@vercel/analytics';
import { formatEther, formatUnits } from 'ethers';
import api from '../utils/api';

function formatBet(weiStr) {
  if (!weiStr) return '—';
  try {
    const n = BigInt(weiStr);
    if (n > BigInt(1e15)) {
      return formatEther(n);
    }
    return formatUnits(n, 6);
  } catch {
    return weiStr;
  }
}

function formatBeginAt(iso) {
  if (!iso) return 'TBD';
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

export default function HomeExploreSections() {
  const [featured, setFeatured] = useState({ loading: true, list: [], error: '' });
  const [events, setEvents] = useState({
    loading: true,
    configured: false,
    items: [],
    compareOddsUrl: null,
    message: '',
    error: '',
  });

  useEffect(() => {
    let cancelled = false;
    api
      .get('/explore/featured-challenges')
      .then((r) => {
        if (!cancelled) {
          setFeatured({
            loading: false,
            list: Array.isArray(r.data?.challenges) ? r.data.challenges : [],
            error: '',
          });
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setFeatured({
            loading: false,
            list: [],
            error: err?.response?.data?.error || err.message || 'Failed to load',
          });
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    api
      .get('/explore/upcoming-events')
      .then((r) => {
        if (cancelled) return;
        const d = r.data || {};
        setEvents({
          loading: false,
          configured: !!d.configured,
          items: Array.isArray(d.events) ? d.events : [],
          compareOddsUrl: d.compareOddsUrl || null,
          message: d.message || '',
          error: d.error || '',
        });
      })
      .catch((err) => {
        if (!cancelled) {
          setEvents({
            loading: false,
            configured: false,
            items: [],
            compareOddsUrl: null,
            message: '',
            error: err?.response?.data?.error || err.message || 'Failed to load',
          });
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="home-explore-wrap">
      <section className="home-explore-section" aria-labelledby="live-ewio-heading">
          <h2 id="live-ewio-heading" className="home-explore-title">
            Live on EWIO
          </h2>
          <p className="home-explore-desc">
            Both stakes locked and game IDs verified. Matches appear when both players agree on the challenge page, or
            when an admin lists them.{' '}
            <Link to="/spotlight">Spotlight</Link>
            {' · '}
            <Link to="/challenges">All challenges</Link>
          </p>
          {featured.loading ? (
            <p className="home-explore-muted">Loading…</p>
          ) : featured.error ? (
            <p className="home-explore-error">{featured.error}</p>
          ) : featured.list.length === 0 ? (
            <p className="home-explore-muted">
              No featured matches right now. After both players deposit and verify their IDs, each can opt in under
              Spotlight on the challenge page (or use the admin listing toggle).
            </p>
          ) : (
            <ul className="home-explore-cards">
              {featured.list.map((c) => (
                <li key={c.challengeId} className="home-explore-card">
                  <div className="home-explore-card-head">
                    <span className="home-explore-game">{c.gameName}</span>
                    <span className="home-explore-stake">{formatBet(c.betPerPlayerWei)} per player</span>
                  </div>
                  <p className="home-explore-players">
                    {c.creatorAddressShort} vs {c.joinerAddressShort}
                  </p>
                  <Link
                    className="home-explore-link"
                    to={`/challenge/${c.challengeId}`}
                    onClick={() => track('home_featured_challenge_open', { challengeId: String(c.challengeId) })}
                  >
                    View challenge →
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </section>

      <section className="home-explore-section home-explore-esports" aria-labelledby="esports-week-heading">
          <h2 id="esports-week-heading" className="home-explore-title">
            Pro esports coming up
          </h2>
          <p className="home-explore-desc">
            Upcoming matches for games you can play on EWIO (LoL, CS2, Dota 2, Rocket League). Data from{' '}
            <a href="https://pandascore.co" target="_blank" rel="noopener noreferrer">
              PandaScore
            </a>
            . Prefer betting on <strong>your own</strong> skill match?{' '}
            <button
              type="button"
              className="home-explore-inline-btn"
              onClick={() => {
                track('home_upcoming_cta_scroll_games');
                const el = document.getElementById('games-section');
                el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }}
            >
              Start a challenge
            </button>
            .
          </p>
          {events.loading ? (
            <p className="home-explore-muted">Loading schedule…</p>
          ) : events.error ? (
            <p className="home-explore-error">{events.error}</p>
          ) : !events.configured ? (
            <p className="home-explore-muted">
              {events.message || 'Esports schedule is not configured on this server (add PANDASCORE_TOKEN).'}
            </p>
          ) : events.items.length === 0 ? (
            <p className="home-explore-muted">No upcoming tier matches in the next window for those titles.</p>
          ) : (
            <ul className="home-explore-events">
              {events.items.map((ev) => (
                <li key={ev.id} className="home-explore-event">
                  <div className="home-explore-event-top">
                    <span className="home-explore-event-game">{ev.gameName}</span>
                    <time className="home-explore-event-time" dateTime={ev.beginAt || undefined}>
                      {formatBeginAt(ev.beginAt)}
                    </time>
                  </div>
                  <p className="home-explore-event-name">{ev.name}</p>
                  {(ev.leagueName || ev.serieName) && (
                    <p className="home-explore-event-meta">
                      {[ev.leagueName, ev.serieName].filter(Boolean).join(' · ')}
                    </p>
                  )}
                  {ev.matchUrl ? (
                    <a
                      className="home-explore-link home-explore-link-subtle"
                      href={ev.matchUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => track('home_esports_stream_click', { matchId: String(ev.id) })}
                    >
                      Stream / info →
                    </a>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
          {events.compareOddsUrl ? (
            <p className="home-explore-odds-note">
              <a
                href={events.compareOddsUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => track('home_compare_odds_click')}
              >
                Where to compare lines (external)
              </a>
              <span className="home-explore-disclaimer">
                {' '}
                — EWIO is peer-to-peer skill wagering, not a sportsbook.
              </span>
            </p>
          ) : (
            <p className="home-explore-disclaimer home-explore-disclaimer-block">
              EWIO does not run bookmaker odds. Pro matches are for context; your wagers on EWIO are always between
              you and your opponent in escrow.
            </p>
          )}
        </section>
    </div>
  );
}
